/**
 * Teams SSO session management utilities.
 *
 * Responsibilities:
 *  1. Bootstrap a backend server-side session from a Teams SSO token
 *     (POST /api/teams/auth/bootstrap).
 *  2. Request a fresh token from the parent frame via postMessage when
 *     the backend session expires.
 *  3. Provide a one-time-retry wrapper for fetch calls that fail with 401.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const isTeamsIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch {
    return true // cross-origin parent — assume iframe
  }
}

const TEAMS_APP_CLIENT_ID = new Set([
  '5e3ce6c0-2b1f-4285-8d4b-75ee78787346',
  '1fec8e78-bce4-4aaf-ab1b-5451cc387264',
])

/** True only for a genuine Teams SSO assertion (azp == the Teams app's client ID). */
export const isTeamsToken = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return TEAMS_APP_CLIENT_ID.has(payload.azp)
  } catch {
    return false
  }
}

// Set to '1' only after a successful Teams OBO bootstrap; cleared on failure
const _TEAMS_SESSION_FLAG = 'teams_session_active'

/** True only when running inside an iframe whose Teams bootstrap succeeded. */
export const isTeamsSession = (): boolean =>
  isTeamsIframe() && sessionStorage.getItem(_TEAMS_SESSION_FLAG) === '1'

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

export type BootstrapResult = { ok: boolean; accessToken?: string; expiresAt?: number }

/**
 * Send the Teams SSO token to the backend bootstrap endpoint.
 * On success the server validates the token, performs the OBO exchange,
 * and sets a `teams_sid` HttpOnly cookie that subsequent requests use.
 */
export const bootstrapTeamsSession = async (teamsToken: string): Promise<BootstrapResult> => {
  try {
    const res = await fetch('/api/teams/auth/bootstrap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // required for the session cookie to be set
      body: JSON.stringify({ teams_token: teamsToken }),
    })
    const body = await res.json().catch(() => ({}))
    if (res.ok) {
      sessionStorage.setItem(_TEAMS_SESSION_FLAG, '1')
    } else {
      console.error('[TeamsAuth] Bootstrap failed:', res.status, body)
      sessionStorage.removeItem(_TEAMS_SESSION_FLAG)
    }
    return { ok: res.ok, accessToken: body.access_token, expiresAt: body.expires_at }
  } catch (err) {
    console.error('[TeamsAuth] Bootstrap request error:', err)
    sessionStorage.removeItem(_TEAMS_SESSION_FLAG)
    return { ok: false }
  }
}

// ---------------------------------------------------------------------------
// Token refresh via postMessage
// ---------------------------------------------------------------------------

const _REFRESH_TIMEOUT_MS = 10_000

/** Ask the parent frame for a fresh Teams SSO token and wait up to 10 s. */
const _requestFreshToken = (): Promise<string> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', handler)
      reject(new Error('[TeamsAuth] Timeout waiting for fresh token from parent'))
    }, _REFRESH_TIMEOUT_MS)

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'akira-token' && event.data?.token) {
        clearTimeout(timer)
        window.removeEventListener('message', handler)
        resolve(event.data.token as string)
      }
    }

    window.addEventListener('message', handler)
    try {
      window.parent.postMessage({ type: 'akira-token-request' }, '*')
    } catch (err) {
      clearTimeout(timer)
      window.removeEventListener('message', handler)
      reject(err)
    }
  })

// Deduplication: concurrent refresh calls share one in-flight promise
let _inflightRefresh: Promise<boolean> | null = null

/**
 * Refresh the backend Teams session:
 *  1. Request a fresh SSO token from the parent frame via postMessage.
 *  2. Update sessionStorage (keeps existing postMessage handler consistent).
 *  3. Call bootstrapTeamsSession() to re-establish the backend session.
 *
 * Concurrent callers share a single in-flight promise so only one refresh
 * request is sent to the backend at a time.
 */
/** Call the backend silent-refresh endpoint. Uses MSAL's cached refresh token — no postMessage needed. */
const _refreshViaBackend = async (): Promise<boolean> => {
  try {
    const res = await fetch('/api/teams/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      // TEAMS_REBOOTSTRAP_REQUIRED means the MSAL cache is exhausted; fall back to postMessage
      if (body?.code === 'TEAMS_REBOOTSTRAP_REQUIRED') return false
      console.error('[TeamsAuth] Backend refresh error:', res.status, body)
      return false
    }
    if (body.access_token) {
      sessionStorage.setItem('akira_injected_token', body.access_token)
      sessionStorage.setItem('akira_token_expiry', String((body.expires_at ?? 0) * 1000))
    }
    return true
  } catch {
    return false
  }
}

export const refreshTeamsSession = (): Promise<boolean> => {
  if (_inflightRefresh) return _inflightRefresh

  _inflightRefresh = (async () => {
    try {
      // Prefer backend silent refresh (MSAL cached refresh token, no parent postMessage needed)
      const silentOk = await _refreshViaBackend()
      if (silentOk) {
        console.log('[TeamsAuth] Session refreshed via backend silent refresh')
        return true
      }

      // MSAL cache exhausted — request a fresh SSO token from parent and re-bootstrap
      console.log('[TeamsAuth] Silent refresh failed, requesting fresh token from parent...')
      const freshToken = await _requestFreshToken()

      if (!isTeamsToken(freshToken)) {
        console.error('[TeamsAuth] Fresh token from parent is not a Teams SSO assertion')
        return false
      }

      const result = await bootstrapTeamsSession(freshToken)
      if (result.ok && result.accessToken) {
        sessionStorage.setItem('akira_injected_token', result.accessToken)
        sessionStorage.setItem('akira_token_expiry', String((result.expiresAt ?? 0) * 1000))
        console.log('[TeamsAuth] Session refreshed via re-bootstrap')
      } else {
        console.error('[TeamsAuth] Session refresh: re-bootstrap failed')
      }
      return result.ok
    } catch (err) {
      console.error('[TeamsAuth] Session refresh failed:', err)
      return false
    } finally {
      _inflightRefresh = null
    }
  })()

  return _inflightRefresh
}

// ---------------------------------------------------------------------------
// Retry wrapper
// ---------------------------------------------------------------------------

/**
 * Execute *fn*, and if it returns a 401 while running inside a Teams iframe,
 * refresh the backend session and retry **once**.
 *
 * Usage:
 *   const response = await withTeamsSessionRetry(() => fetch('/api/...', {...}))
 */
export const withTeamsSessionRetry = async (
  fn: () => Promise<Response>
): Promise<Response> => {
  const response = await fn()

  if (response.status === 401 && isTeamsIframe()) {
    console.warn('[TeamsAuth] 401 in iframe — refreshing session and retrying')
    const refreshed = await refreshTeamsSession()
    if (refreshed) {
      return fn() // single retry
    }
  }

  return response
}
